import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtUSD, imgSrc } from "@/lib/api";
import YachtForm from "@/pages/admin/YachtForm";
import { ConfirmDelete } from "@/pages/admin/shared";
import type { Yacht } from "@/types";

export default function AdminYachts() {
  const { data = [], refetch } = useQuery<Yacht[]>({ queryKey: ["admin-yachts"], queryFn: async () => (await api.get("/admin/yachts")).data });
  const [editing, setEditing] = useState<Yacht | null | "new">(null);

  const remove = async (id: string) => {
    await api.delete(`/admin/yachts/${id}`);
    toast.success("Yate eliminado");
    refetch();
  };

  return (
    <div className="max-w-6xl" data-testid="admin-yachts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#0B192C]">Yates</h1>
          <p className="mt-2 text-sm text-slate-500">Los yates marcados como “Ejemplo” tienen datos y precios de muestra: edítalos con tu información real.</p>
        </div>
        <button onClick={() => setEditing("new")} data-testid="admin-yacht-create-btn" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-[#0B192C] hover:bg-[#F3E5AB]"><Plus className="h-4 w-4" />Nuevo yate</button>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {data.map((y) => (
          <div key={y.id} data-testid={`admin-yacht-${y.id}`} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="relative aspect-[16/9]">
              <img src={imgSrc(y.photos[0])} alt={y.name} className="h-full w-full object-cover" />
              <div className="absolute top-2 left-2 flex gap-1.5">
                {y.is_sample && <span className="rounded bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase text-[#0B192C]" data-testid={`admin-yacht-sample-${y.id}`}>Ejemplo</span>}
                {!y.active && <span className="rounded bg-slate-700 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Oculto</span>}
              </div>
            </div>
            <div className="p-4">
              <p className="font-display text-xl text-[#0B192C]">{y.name}</p>
              <p className="text-xs text-slate-500">{y.length_ft} ft · {y.capacity} personas · {y.photos.length} fotos</p>
              <p className="mt-2 text-xs text-slate-600">4h {fmtUSD(y.prices.h4)} · 6h {fmtUSD(y.prices.h6)} · 8h {fmtUSD(y.prices.h8)} · Pern. {fmtUSD(y.prices.overnight)}</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setEditing(y)} data-testid={`admin-yacht-edit-${y.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:border-[#0B192C]"><Pencil className="h-3.5 w-3.5" />Editar</button>
                <ConfirmDelete title={`¿Eliminar ${y.name}?`} onConfirm={() => remove(y.id)}>
                  <button data-testid={`admin-yacht-delete-${y.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" />Eliminar</button>
                </ConfirmDelete>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="yacht-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "new" ? "Nuevo yate" : "Editar yate"}</DialogTitle></DialogHeader>
          {editing && <YachtForm key={editing === "new" ? "new" : editing.id} yacht={editing === "new" ? undefined : editing} onSaved={() => { setEditing(null); refetch(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
